import React, { FC } from 'react';
import styled from 'styled-components';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import WarningIcon from 'src/shared/material_icons/alert/warning.svg';
import { T } from 'src/shared/helpers';

const WarningTrigger = styled.span`
    display: inline-flex;
    width: 20px;
    height: 20px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    color: var(--composer-warn-icon, #a85a08);
    border-radius: 5px;

    svg {
        width: 15px;
        height: 15px;
        fill: currentColor;
    }
`;

const WarningList = styled.div`
    display: grid;
    width: min(340px, calc(100vw - 48px));
    max-height: min(320px, calc(100vh - 48px));
    overflow: hidden;
    color: var(--composer-fg, #1e2a38);
    font-size: 11.5px;
    line-height: 15px;

    header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 5px 6px 6px;
        color: var(--composer-warn-ink, #874b0b);
        border-bottom: 1px solid var(--composer-warn-line, #f0dcc5);
        font-weight: 700;
        font-size: 10px;
        letter-spacing: 0.02em;
        text-transform: uppercase;
    }

    header span:last-child {
        display: inline-flex;
        min-width: 18px;
        height: 18px;
        align-items: center;
        justify-content: center;
        padding: 0 5px;
        background: var(--composer-warn-wash, #fff0df);
        border-radius: 9px;
        font-size: 10px;
    }

    ol {
        display: grid;
        max-height: 270px;
        margin: 0;
        padding: 0;
        overflow-y: auto;
        list-style: none;
    }

    li {
        display: grid;
        grid-template-columns: 18px minmax(0, 1fr);
        gap: 6px;
        align-items: start;
        padding: 6px;

        & + li {
            border-top: 1px solid var(--composer-line-soft, #eef1f3);
        }
    }

    li span:first-child {
        display: inline-flex;
        width: 17px;
        height: 17px;
        align-items: center;
        justify-content: center;
        color: var(--composer-warn-ink, #874b0b);
        background: var(--composer-warn-wash, #fff0df);
        border-radius: 50%;
        font-size: 9px;
        font-weight: 700;
    }
`;

interface ComposerValidationTooltipProps {
    messages: string[];
    ariaLabel?: string;
}

const ComposerValidationTooltip: FC<ComposerValidationTooltipProps> = ({ messages, ariaLabel }) => {
    const uniqueMessages = Array.from(new Set(messages.filter(Boolean)));
    if (uniqueMessages.length === 0) return null;

    const label = ariaLabel || `${uniqueMessages.length} configuration issue${uniqueMessages.length === 1 ? '' : 's'}. ${uniqueMessages.join('. ')}`;
    return (
        <ZTooltip
            compact
            interactive
            maxWidth={360}
            html={
                <WarningList>
                    <header>
                        <span>{uniqueMessages.length === 1 ? T._('Configuration issue', 'Admin') : T._('Configuration issues', 'Admin')}</span>
                        <span aria-label={T._(`${uniqueMessages.length} issues`, 'Admin')}>{uniqueMessages.length}</span>
                    </header>
                    <ol>
                        {uniqueMessages.map((message, index) => (
                            <li key={message}>
                                <span aria-hidden='true'>{index + 1}</span>
                                <span>{message}</span>
                            </li>
                        ))}
                    </ol>
                </WarningList>
            }
        >
            <WarningTrigger role='img' aria-label={label} tabIndex={0}>
                <WarningIcon />
            </WarningTrigger>
        </ZTooltip>
    );
};

export default ComposerValidationTooltip;
